// Field help for the billing screens (Invoices, the Close dialog, Rate plans, Model prices). Keys
// are the ids passed to <Field help="…">; every rendered label must have one
// (tests/unit/help.test.ts). Copy follows docs/guides/revenue-billing.md and admin-guide.md.
import type { HelpTopic } from "../topic";

export const BILLING_TOPICS = {
  // ---- Invoices (billing periods across customers) ----
  "invoices.month": {
    title: "Month",
    body: "The UTC calendar month whose invoices are listed, one row per customer. Every customer has a month of its own, opened automatically by Nexus.",
    example: "August 2026 lists every customer's August: the ones already closed and the ones still open.",
    article: "closing-a-month",
  },
  "invoices.status": {
    title: "Status",
    body: "Open months can still change: usage keeps arriving and the invoice is a preview. Closed months are final — their lines are frozen and never change.",
    example: "On 2 September, August is usually still Open until someone closes it; July is Closed.",
    article: "closing-a-month",
  },
  "invoices.ledgerTotal": {
    title: "Ledger total",
    body: "The month's usage at AWS list price, plus late usage carried in from earlier closed months. It is set when the month closes, so an open month shows a dash. What the customer pays (with the rate plan's markup) is on the invoice.",
    example: "A closed month with $950.00 of usage and $12.40 of late usage from July has a ledger total of $962.40.",
    article: "closing-a-month",
    section: "invoices-and-statements",
  },
  "invoices.requests": {
    title: "Requests",
    body: "How many model requests the customer's proxies counted in the month, as recorded at close.",
    example: "182,406 requests in August across all of the customer's installations.",
    article: "closing-a-month",
  },
  "invoices.unmetered": {
    title: "Not fully metered",
    body: "Requests the proxy counted but could not meter completely: unmetered ones have no token counts, partially metered ones lost some. Non-zero figures are worth a look in Reconciliation before trusting the invoice.",
    example: "3 unmetered + 1 partially metered out of 182,406 requests shows as 4.",
    article: "reconciliation",
    section: "completeness",
  },
  "invoices.closed": {
    title: "Closed",
    body: "When the month was closed, and whether it was forced past unverified or missing model prices. A forced close keeps its reason on the record.",
    example: "Closed 3 Sep 2026, forced: “Finance accepted the placeholder Opus rate for August.”",
    article: "closing-a-month",
    section: "forcing",
  },

  // ---- Close dialog ----
  "close.force": {
    title: "Close anyway",
    body: "Closes the month even though some usage is priced at an unverified model price, or a model has no price at all. It never skips the month not having ended or usage still being counted. The close is recorded as forced, with your reason.",
    example: "August used a model whose price row is not verified yet. Finance accepts that rate, so you tick this and give the reason.",
    article: "closing-a-month",
    section: "forcing",
  },
  "close.forceReason": {
    title: "Reason",
    body: "Why the month is being closed anyway, in 10 to 500 characters. It is stored with the month and in the audit log, so write it for someone reading it a year from now.",
    example: "Finance (J. Ortiz) accepted the placeholder rate for openai.gpt-oss-120b cache reads for August 2026; see PRICE_EVIDENCE.md.",
    article: "closing-a-month",
    section: "forcing",
  },

  // ---- Rate plans ----
  "rateplan.name": {
    title: "Plan name",
    body: "The name people see when they assign this plan to a customer. Customers never see it; their invoice lists charges, not the plan.",
    example: "Standard cost + 30%, Enterprise volume, Internal (not billed).",
    article: "rate-plans",
  },
  "rateplan.description": {
    title: "Description",
    body: "A note for your team about who this plan is for and what it charges, up to 500 characters. It shows with the plan when it is being chosen.",
    example: "For customers who pay AWS themselves and use us for support only.",
    article: "rate-plans",
  },
  "rateplan.status": {
    title: "Status",
    body: "Draft plans can be edited. Activating a plan freezes it so it can be assigned to customers; retiring it stops it being offered. To change an active plan, clone it and assign the new one from a later month.",
    example: "Draft “Enterprise 2027” is edited, activated on 1 December, then assigned to Hooli from January.",
    article: "rate-plans",
    section: "plan-lifecycle",
  },
  "rateplan.markupBasis": {
    title: "Markup applies to",
    body: "Which AWS cost the markup is added to. AWS list price uses the published Bedrock rate for each token, so invoices are predictable. Actual AWS bill uses what AWS charged after discounts and credits, from the customer's Cost and Usage Report, so the invoice can only be written once that month's AWS bill is final. Use it only for customers you trust with that report.",
    example: "$1,000 of tokens at list price, and AWS gave a 10 % discount. With 30 % markup: list price bills $1,300; the actual bill bills $900 × 1.3 = $1,170.",
    article: "pricing-and-markup",
    section: "cost-basis",
  },
  "rateplan.markup": {
    title: "Markup",
    body: "The percentage added on top of AWS cost, from 0 % to 1,000 %. It prices every model unless a model exception says otherwise.",
    example: "Markup 30 %: $1,000 of AWS cost bills $1,300. Your profit is $300, a 23.1 % margin on the invoice.",
    article: "pricing-and-markup",
    section: "markup-is-not-margin",
  },
  "rateplan.monthlyMinimum": {
    title: "Monthly minimum",
    body: "The least a customer's usage is invoiced in a month. If usage after markup and volume discounts comes to less, a top-up line raises it to this amount. It is waived in a month with an active trial. Leave it at $0 for no minimum.",
    example: "Minimum $500. A quiet month with $200 of billed usage gets a $300 top-up and is invoiced $500 (plus any platform fee).",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.platformFee": {
    title: "Platform fee per month",
    body: "A flat charge added to every monthly invoice, whatever the usage. Leave it at $0 for none.",
    example: "Fee $250 and $1,300 of billed usage: the invoice is $1,550.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.exceptions": {
    title: "Model exceptions",
    body: "Different pricing for a model or a kind of token: its own markup, or a fixed price per million tokens that ignores AWS cost. When several apply, the most specific one wins — a model and token class, then a model, then a token class for every model (*). Models without an exception use the plan markup.",
    example: "Claude Opus at 20 % markup and everything else at 30 %; or Claude Sonnet output tokens at a fixed $18.00 per million.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.exceptionModel": {
    title: "Model",
    body: "The Bedrock model id the exception applies to, exactly as it appears in Model prices, or * for every model.",
    example: "anthropic.claude-opus-4-1-20250805-v1:0 for Claude Opus 4.1 only; * with token class Output for every model's output tokens.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.exceptionTokenClass": {
    title: "Token class",
    body: "Which tokens the exception prices: input, output, cache reads, 5-minute or 1-hour cache writes, or all of them.",
    example: "Output only: input tokens of that model keep the plan markup, output tokens use the exception.",
    article: "model-prices",
  },
  "rateplan.exceptionPrice": {
    title: "Price",
    body: "Either a markup % on AWS cost, or a fixed dollar price per million tokens. A fixed price ignores AWS cost entirely, on either markup basis.",
    example: "Fixed $3.00 per million: 2,500,000 tokens are invoiced $7.50 whatever AWS charged.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.tiers": {
    title: "Volume discounts",
    body: "Graduated discounts on the month's usage price: each tier's discount applies only to the spend within that tier. The first tier starts at $0 and each next one starts higher.",
    example: "0 % from $0, 10 % from $1,000, 20 % from $5,000. $7,000 of usage: $4,000 × 10 % + $2,000 × 20 % = an $800 discount.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.tierFrom": {
    title: "From monthly spend",
    body: "Where the tier starts, in dollars of the month's usage price (after markup). The first tier must start at $0.",
    example: "From $5,000: the discount applies to every dollar of usage price above $5,000.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.tierDiscount": {
    title: "Discount",
    body: "The percentage taken off the usage price within this tier, from 0 % to 100 %.",
    example: "10 % on the $4,000 between $1,000 and $5,000 is a $400 discount.",
    article: "rate-plans",
    section: "rates-and-tiers",
  },
  "rateplan.workedExample": {
    title: "What a customer pays",
    body: "An illustration of this plan's settings on one amount of AWS usage: plan markup, volume discounts, the monthly minimum and the platform fee, in the order Nexus applies them. Model exceptions, trials and adjustments are left out; real invoices are priced by Nexus per model, token class and day.",
    example: "$1,000 of usage at 30 % markup with no fee: the invoice is $1,300, profit $300 — a 30 % markup and a 23.1 % margin.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "rateplan.exampleUsage": {
    title: "Usage amount",
    body: "A month of AWS usage, in dollars on the plan's markup basis, to try the plan on.",
    example: "Type 25000 to see what a $25,000 month is invoiced under this plan.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },

  // ---- Model prices ----
  "prices.search": {
    title: "Model",
    body: "Shows only the rows of one Bedrock model. Type its full model id exactly as AWS names it.",
    example: "anthropic.claude-sonnet-4-5-20250929-v1:0 lists every dated price of Claude Sonnet 4.5.",
    article: "model-prices",
  },
  "prices.validOn": {
    title: "Valid on",
    body: "Shows, for each model, only the price in force on that UTC day. Models with no price yet on that day are left out. Leave it empty for every row.",
    example: "Valid on 2026-08-15 shows the rate each model's August 15 usage was priced at.",
    article: "model-prices",
  },
  "prices.validFrom": {
    title: "Valid from",
    body: "The UTC day the price takes effect, at 00:00. Left empty, it is tomorrow. A date of today or earlier re-prices open months' history the next time it is read; months already closed are never re-priced and refuse the change.",
    example: "Valid from 2026-10-01: usage from October 1 onwards is priced at the new rate, September keeps the old one.",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.valid": {
    title: "Valid",
    body: "The UTC days this price applies: from its valid-from day until the day before the model's next price takes effect. The newest price of a model stays in force (“now”) until a later one is added.",
    example: "Rows from 1 Sep 2025 and 23 Sep 2026: the first is valid 1 Sep 2025 – 22 Sep 2026, the second 23 Sep 2026 – now.",
    article: "model-prices",
  },
  "prices.bedrockModelId": {
    title: "Bedrock model id",
    body: "The model id exactly as Bedrock reports it in usage, including the version suffix. A model with no price for a day is left unpriced, never given a default.",
    example: "anthropic.claude-sonnet-4-5-20250929-v1:0",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.rates": {
    title: "Dollars per million tokens",
    body: "The AWS list price of each kind of token, in dollars per million tokens, up to six decimals. Input, output, cache read and 5-minute cache write are required; a 1-hour cache write left empty costs the same as a 5-minute one.",
    example: "Claude Sonnet 4.5: input $3.00, output $15.00, cache read $0.30, cache write (5 min) $3.75, cache write (1 hour) $6.00.",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.input": {
    title: "Input",
    body: "The AWS list price, in dollars per million input (prompt) tokens.",
    example: "$3.00 per million: a 2,000-token prompt costs $0.006.",
    article: "model-prices",
  },
  "prices.output": {
    title: "Output",
    body: "The AWS list price, in dollars per million output (generated) tokens.",
    example: "$15.00 per million: a 1,000-token answer costs $0.015.",
    article: "model-prices",
  },
  "prices.cacheRead": {
    title: "Cache read",
    body: "The AWS list price per million tokens read back from the prompt cache, usually a small fraction of the input price.",
    example: "$0.30 per million: re-reading a 50,000-token cached prompt costs $0.015.",
    article: "model-prices",
  },
  "prices.cacheWrite": {
    title: "Cache write (5 min)",
    body: "The AWS list price per million tokens written to the prompt cache with the standard 5-minute lifetime.",
    example: "$3.75 per million: caching a 50,000-token prompt costs $0.1875.",
    article: "model-prices",
  },
  "prices.cacheWrite1h": {
    title: "Cache write (1 hour)",
    body: "The AWS list price per million tokens written to the prompt cache with a 1-hour lifetime. Empty means the same price as a 5-minute write.",
    example: "$6.00 per million for 1-hour writes, where 5-minute writes are $3.75.",
    article: "model-prices",
  },
  "prices.verified": {
    title: "Verified",
    body: "Whether the rates were checked against the AWS Price List, row by row. A verified price needs a source URL and a retrieval date. Closing a month priced at an unverified row has to be forced with a reason.",
    example: "The seeded rows read from the AWS Price List on 2026-09-23 are verified; an older placeholder row is not.",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.source": {
    title: "Source",
    body: "Who or what set the price, for the record, linked to the page it was read from when there is one. The chip says whether the rates were verified against the AWS Price List.",
    example: "AWS Price List bulk offer file, or “Finance: EDP rate card 2026”.",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.sourceUrl": {
    title: "Source URL",
    body: "The official page or AWS Price List offer file the rates were read from. Required when the price is verified.",
    example: "https://pricing.us-east-1.amazonaws.com/offers/v1.0/aws/AmazonBedrock/current/index.json",
    article: "model-prices",
    section: "adding-a-price",
  },
  "prices.retrievedOn": {
    title: "Retrieved on",
    body: "The day the source was read, as YYYY-MM-DD. Required when the price is verified.",
    example: "The seeded rates were read from the AWS Price List on 2026-09-23, so that is their retrieval date.",
    article: "model-prices",
    section: "adding-a-price",
  },
} satisfies Record<string, HelpTopic>;
